import { useCallback, useEffect, useState } from "react";
import { adminApi, downloadCsv } from "./adminApi";

const STATUSES = ["New Lead", "Test Completed", "Consultation Interested", "Consultation Booked", "Consultation Completed", "Viva Completed", "Cashback Approved", "Cashback Paid", "Follow-up Required", "Converted to Student"];
const REWARD_STATUSES = ["Locked", "Eligible for Review", "Approved for Payment", "Payment Completed", "Payment Failed"];

const fmtDate = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

export default function AdminStudents() {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [eligible, setEligible] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});
  const [previewStudent, setPreviewStudent] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [hwStudents, setHwStudents] = useState([]);

  useEffect(() => {
    adminApi.get("/hw/admin/students").then(({ data }) => setHwStudents(data.students || [])).catch(() => setHwStudents([]));
  }, []);

  const load = useCallback(() => {
    adminApi.get("/admin/test/overview").then(({ data }) => setOverview(data)).catch(() => {});
    adminApi.get("/admin/test/funnel").then(({ data }) => setFunnel(data)).catch(() => {});
    const params = {};
    if (search) params.q = search;
    if (status) params.status = status;
    if (eligible) params.eligible = eligible;
    adminApi.get("/admin/test/students", { params }).then(({ data }) => setRows(data.students || [])).catch(() => {});
  }, [search, status, eligible]);

  useEffect(() => { load(); }, [load]);

  const saveStudent = async (studentId, leadStatus) => {
    try {
      await adminApi.patch(`/admin/students/${studentId}`, { lead_status: leadStatus, notes: notes[studentId] });
      load();
    } catch (e) { /* ignore */ }
  };

  const previewReport = async () => {
    if (!previewStudent) return;
    setPreviewLoading(true);
    try {
      const { data } = await adminApi.get(`/hw/admin/parent-report/preview/${previewStudent}`);
      setPreviewHtml(data.html ? { html: data.html, name: data.student_name } : { html: null, name: data.student_name, note: data.note });
    } catch (e) {
      setPreviewHtml({ html: null, name: "", note: "Could not load preview." });
    } finally { setPreviewLoading(false); }
  };

  const cards = overview ? [
    ["Total Students", overview.total_students ?? 0], ["Tests Taken", overview.total_tests ?? 0],
    ["Cashback Eligible", overview.cashback_eligible ?? 0], ["Consultations", overview.consultations ?? 0],
    ["Rewards Paid", overview.rewards_paid ?? 0],
  ] : [];

  return (
    <div data-testid="admin-students">
      {overview && (
        <div className="stat-cards" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }} data-testid="test-overview">
          {cards.map(([label, value]) => (
            <div className="stat-card" key={label}><b style={{ fontSize: 22 }}>{value}</b><span>{label.toUpperCase()}</span></div>
          ))}
        </div>
      )}
      {funnel && (
        <div className="admin-card" data-testid="test-funnel" style={{ marginBottom: 18 }}>
          <div className="admin-row" style={{ justifyContent: "space-between" }}>
            <h4 style={{ margin: 0 }}>Test Funnel</h4>
            <span className="lead-sub">Registered → Tested → Consult → Cashback</span>
          </div>
          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            {(funnel.steps || []).map((step) => (
              <div key={step.key} data-testid={`funnel-step-${step.key}`} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 130, fontSize: 12 }}>{step.label}</span>
                <div style={{ flex: 1, background: "#1e2d42", borderRadius: 6, height: 8, overflow: "hidden" }}>
                  <div style={{ width: `${step.conversion}%`, height: "100%", background: "#f77f00", borderRadius: 6 }} />
                </div>
                <span className="lead-sub" style={{ width: 52, textAlign: "right" }} data-testid={`funnel-conv-${step.key}`}>{step.conversion}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="admin-row" style={{ flexWrap: "wrap", gap: 8 }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, WhatsApp or Student ID…" data-testid="ts-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ts-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={eligible} onChange={(e) => setEligible(e.target.value)} data-testid="ts-eligible-filter">
          <option value="">Everyone</option>
          <option value="yes">Cashback eligible</option>
          <option value="no">Not eligible</option>
        </select>
        <button className="admin-btn" onClick={() => downloadCsv("students")} data-testid="ts-export-students">Export students CSV</button>
        <button className="admin-btn" onClick={() => downloadCsv("bookings")}>Bookings CSV</button>
        <button className="admin-btn" onClick={() => downloadCsv("rewards")}>Rewards CSV</button>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <select className="admin-input" style={{ minWidth: 150, padding: "6px 8px", fontSize: 12 }} value={previewStudent}
                  onChange={(e) => setPreviewStudent(e.target.value)} data-testid="ts-preview-student">
            <option value="">Student for report…</option>
            {hwStudents.map((s) => <option key={s.student_id} value={s.student_id}>{s.name}</option>)}
          </select>
          <button className="admin-btn" onClick={previewReport} disabled={previewLoading || !previewStudent} data-testid="ts-preview-report">
            {previewLoading ? "Loading…" : "Preview parent report"}
          </button>
        </div>
      </div>
      {previewHtml && (
        <div className="admin-card" style={{ marginTop: 14 }} data-testid="ts-preview-panel">
          <div className="admin-row" style={{ justifyContent: "space-between" }}>
            <b>Parent report preview — {previewHtml.name}</b>
            <button className="admin-btn" onClick={() => setPreviewHtml(null)} data-testid="ts-preview-close">Close</button>
          </div>
          {previewHtml.html
            ? <div style={{ marginTop: 10, background: "#fff", borderRadius: 8, padding: 16, color: "#0b2545" }}
                   dangerouslySetInnerHTML={{ __html: previewHtml.html }} data-testid="ts-preview-html" />
            : <p className="lead-sub" style={{ marginTop: 10 }} data-testid="ts-preview-note">{previewHtml.note || "No report this week."}</p>}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="admin-empty">No students yet.</div>
      ) : (
        <div className="table-scroll">
          <table className="leads-table" data-testid="ts-table">
            <thead>
              <tr><th>STUDENT</th><th>CLASS / EXAM</th><th>TEST RESULT</th><th>CASHBACK</th><th>STATUS</th><th>REGISTERED</th></tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const isOpen = expanded === s.student_id;
                return [
                  <tr className="lead-row" key={s.student_id} onClick={() => setExpanded(isOpen ? null : s.student_id)} data-testid={`ts-row-${s.student_id}`}>
                    <td><div className="lead-name">{s.name}</div><div className="lead-sub">{s.student_id} · {s.whatsapp}</div></td>
                    <td>{s.current_class}<div className="lead-sub">{s.target_exam}</div></td>
                    <td>{s.test_result ? `${s.test_result.score}/${s.test_result.total}` : "—"}<div className="lead-sub">{s.test_result ? `${s.test_result.percent}%` : ""}</div></td>
                    <td>{s.reward_status || "—"}</td>
                    <td><span className={`status-pill ${s.lead_status === "Converted to Student" ? "approved" : s.lead_status === "Cashback Paid" ? "success" : "review"}`}>{s.lead_status}</span></td>
                    <td>{fmtDate(s.created_at)}</td>
                  </tr>,
                  isOpen && (
                    <tr className="lead-detail" key={`${s.student_id}-d`}>
                      <td colSpan={6}>
                        <div className="lead-detail-grid">
                          <div>
                            <h4>Details</h4>
                            <p><b>Email:</b> {s.email || "—"} · <b>School:</b> {s.school || "—"}</p>
                            <p><b>Promo consent:</b> {s.promo_consent ? "Yes" : "No"}</p>
                            {s.booking && <p><b>Booking:</b> {s.booking.date} {s.booking.slot} · Consult {s.booking.consultation_done ? "✓" : "pending"} · Viva {s.booking.viva_done ? "✓" : "pending"}</p>}
                            <label className="admin-label">LEAD STATUS
                              <select className="admin-input" defaultValue={s.lead_status} id={`st-${s.student_id}`} data-testid={`ts-status-${s.student_id}`}>
                                {STATUSES.map((st) => <option key={st}>{st}</option>)}
                              </select>
                            </label>
                          </div>
                          <div>
                            <h4>Internal notes</h4>
                            <textarea rows={4} value={notes[s.student_id] ?? s.notes ?? ""} onChange={(e) => setNotes((c) => ({ ...c, [s.student_id]: e.target.value }))} data-testid={`ts-notes-${s.student_id}`} />
                            <button className="admin-btn" onClick={() => saveStudent(s.student_id, document.getElementById(`st-${s.student_id}`).value)} data-testid={`ts-save-${s.student_id}`}>Save</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
